import React, {useEffect, useMemo, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {toast} from 'react-toastify'
import {useAppDispatch, useAppSelector} from 'setup/redux/Hooks'
import {ToastDefaultConfig} from 'setup/toast/ToastConfig'
import {selectUserMetaData} from 'app/modules/auth'
import {serviceManagementAction} from '../../redux/ServiceManagementSlice'
import {CentroCategoryInfoModel, CentroServiceConfigQry} from '../../models'
import {Modal} from 'react-bootstrap-v5'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'app/utils/helpers'
import {
  FORM_ICONS,
  FieldLabel,
  SelectField,
  TextAreaField,
  TextField,
  ToggleRow,
  VatTreatment,
} from './ServiceFormFields'
import './style.scss'
import CategoryPaymentSettings from '../category-modal/CategoryPaymentSettings'
import {CategoryPaymentConfig} from '../../models'
import { useServiceForm } from '../service/hooks/useServiceForm'
import { getApiErrorMessage } from '../../utils/apiError'
import { roundMoney } from '../../utils/money'
import { buildIconFormData, stripIconSasToken } from '../../utils/serviceIcon'

const emptyToUndefined = (schema: Yup.NumberSchema) =>
  schema.transform((value, original) => (original === '' ? undefined : value))

const toNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

interface AddMajorServiceModalProps {
  isShow: boolean
  onHide: () => void
  categoryInfoId?: string
  b8ShareCategoryInfoId?: string
  onSaved: (b8ShareCategoryInfoId?: string) => void
  isLoading?: boolean
  onSave?: () => void
  onOpenAdvancedPricing?: () => void
  mode?: 'add' | 'edit'
  nodeId?: string
  serviceId?: number
  isCustom?: boolean
  initialNameEn?: string
  initialNameAr?: string
  initialStatus?: boolean
  initialIcon?: string | null
  // Resolved values from the tree; a B8ak service's own config stays empty until overridden.
  initialDuration?: number | null
  initialWorkersNeeded?: number | null
  initialPrice?: number | null
  // Edit only: the B8ak source is gone, so only the description and Receive B8ak orders stay editable.
  isRemovedFromB8ak?: boolean
  statusLockMessage?: string,
  sourceType?: string
}

const AddMajorServiceModal: React.FC<AddMajorServiceModalProps> = ({
  isShow,
  onHide,
  categoryInfoId,
  b8ShareCategoryInfoId,
  onSaved,
  isLoading = false,
  onSave,
  onOpenAdvancedPricing,
  mode = 'add',
  nodeId,
  serviceId,
  isCustom = true,
  initialNameEn,
  initialNameAr,
  initialStatus,
  initialIcon,
  statusLockMessage,
  initialDuration,
  initialWorkersNeeded,
  initialPrice,
  isRemovedFromB8ak = false,
  sourceType
}) => {
  const {t, i18n} = useTranslation()
  const dispatch = useAppDispatch()
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isChangeCategory, setIsChangeCategory] = useState<boolean>(false)
  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(false)
  const [selectedCategory, setSelectedCategory] = useState<CentroCategoryInfoModel | undefined>(undefined)
  const userMetaData = useAppSelector(selectUserMetaData)
  const vatPercent = (userMetaData?.vatValue || 15) / 100

  const isArabic = i18n.language?.toLowerCase().startsWith('ar')

  const isEdit = mode === 'edit'
  // A B8ak service takes its name, pricing and scheduling from B8ak or its Category;
  // the provider only edits descriptions, icon and the Centro visibility toggles.
  const isB8akService = isEdit && !isCustom

  const initialValues = useMemo(
    () => ({
      id: 0,
      categoryInfoId: '',
      nameAr: '',
      nameEn: '',
      subTitleTextEn: '',
      subTitleTextAr: '',
      icon: '' as any,
      svgIcon: '',
      status: 0,
      packageWorkFlowId: 0,
      visitDuration: '',
      // Existing backend field: only the UI label is renamed to 'Workers Needed'.
      numberOfWorkers: '',
      price: '',
      priceWithVat: '',
      vatTreatment: 'excl' as VatTreatment,
      packageWeeks: 1,
      weeklyVisits: 1,
      rescheduleWindowDays: 7,
      rescheduleBeforeMinutes: 720,
      rescheduleLimit: 2,
      oneTimeForAllDays: 0,
      enableRescheduling: 0,
      rescheduleFeeAmount: '',
      rescheduleFeeAmountWithVat: '',
      rescheduleVat: 'excl' as VatTreatment,
      activeFavoriteWorker: 0,
      receiveB8akOrders: 0,
      hasBookingPage: null as boolean | null,
      published: 0,
    }),
    []
  )

  const validationSchema = useMemo(() => {
    // Read-only B8ak fields cannot be corrected by the provider, so they are not validated.
    if (isB8akService) return Yup.object({})

    return Yup.object({
      // Only an add needs the category; the update payload never sends it.
      ...(!isEdit && {
        categoryInfoId: Yup.string()
          .trim()
          .required(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CATEGORY_REQUIRED')),
      }),
      nameEn: Yup.string()
        .trim()
        .required(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME_EN_REQUIRED')),
      nameAr: Yup.string()
        .trim()
        .required(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME_AR_REQUIRED')),
      // Optional; a value that is entered must still be valid.
      visitDuration: emptyToUndefined(Yup.number())
        .typeError(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DURATION_INVALID'))
        .integer(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DURATION_INVALID'))
        .moreThan(0, t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DURATION_MIN')),
      numberOfWorkers: emptyToUndefined(Yup.number())
        .typeError(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.WORKERS_INVALID'))
        .moreThan(0, t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.WORKERS_MIN')),
      price: emptyToUndefined(Yup.number())
        .typeError(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PRICE_INVALID'))
        .min(0, t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PRICE_MIN')),
      packageWeeks: emptyToUndefined(Yup.number())
        .min(1, t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PACKAGE_WEEKS_MIN'))
        .required(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PACKAGE_WEEKS_REQUIRED')),
      weeklyVisits: emptyToUndefined(Yup.number())
        .min(1, t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.WEEKLY_VISITS_MIN'))
        .required(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.WEEKLY_VISITS_REQUIRED')),
      rescheduleWindowDays: Yup.number().min(1).required(t('COMMON.REQUIRED')),
      rescheduleBeforeMinutes: Yup.number().min(0).required(t('COMMON.REQUIRED')),
    })
  }, [t, isB8akService, isEdit])

  const formik = useFormik<any>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (formValues) => {
      // Price is stored as typed, with vatTreatment saying how to read it
      const isIncl = formValues.vatTreatment === 'incl'
      const isRescheduleIncl = formValues.rescheduleVat === 'incl'
      const nameAr = String(formValues.nameAr).trim()
      const nameEn = String(formValues.nameEn).trim()
      // Only a custom Major can be moved to another Category.
      const isCategoryChanged =
        isEdit && isCustom && formValues.categoryInfoId !== (b8ShareCategoryInfoId ?? '')

      const done = (res: unknown, err: unknown) => {
        setIsSaving(false)
        if (err || !res) {
          toast.error(getApiErrorMessage(err, t('COMMON.PLEASE_TRY_AGAIN')), ToastDefaultConfig())
          return
        }
        toast.success(
          t(
            isEdit
              ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_UPDATED'
              : 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_ADDED'
          ),
          ToastDefaultConfig()
        )
        onSaved(isEdit && !isCategoryChanged ? undefined : formValues.categoryInfoId)
        onSave?.()
        onHide()
      }

      const buildPayload = (icon: string | null) => ({
        status: Boolean(formValues.status),
        duration: toNumberOrNull(formValues.visitDuration),
        workersNeeded: toNumberOrNull(formValues.numberOfWorkers),
        price: toNumberOrNull(isIncl ? formValues.priceWithVat : formValues.price),
        vatTreatment: isIncl ? 1 : 0,
        descriptionAr: formValues.subTitleTextAr || null,
        descriptionEn: formValues.subTitleTextEn || null,
        icon,
        activeFavoriteWorker: Boolean(formValues.activeFavoriteWorker),
        receiveB8akOrders: Boolean(formValues.receiveB8akOrders),
        hasBookingPage: formValues.hasBookingPage ?? null,
        // CNT-143 moved these to Sub-only on the backend (a Major is no longer booked as a
        // package); sending them here now 400s every Major save. Commented out for now -
        // the Major form has no UI for them anyway - until recurring/rescheduling on the
        // Major is either removed for good or the backend keeps accepting it.
        // packageWeeks: toNumberOrNull(formValues.packageWeeks),
        // weeklyVisits: toNumberOrNull(formValues.weeklyVisits),
        // rescheduleWindowDays: toNumberOrNull(formValues.rescheduleWindowDays),
        // rescheduleBeforeMinutes: toNumberOrNull(formValues.rescheduleBeforeMinutes),
        // rescheduleLimit: toNumberOrNull(formValues.rescheduleLimit),
        // oneTimeForAllDays: Boolean(formValues.oneTimeForAllDays),
        // enableRescheduling: Boolean(formValues.enableRescheduling),
        // rescheduleFeeAmount: toNumberOrNull(
        //   isRescheduleIncl ? formValues.rescheduleFeeAmountWithVat : formValues.rescheduleFeeAmount
        // ),
        // rescheduleVatTreatment: isRescheduleIncl ? 1 : 0,
      })

      const save = (icon: string | null) => {
        const shared = buildPayload(icon)

        if (!isEdit) {
          // CNT-148 - a new Major is created under a predefined B8ak category
          // via the real AddCustomMajor flow, not mock data.
          dispatch(
            serviceManagementAction.addCentroCategoryService({
              data: {
                b8ShareCategoryInfoId: formValues.categoryInfoId,
                serviceLevel: 1,
                serviceId: null,
                subServiceId: null,
                nameAr,
                nameEn,
                ...shared,
              },
              callback: done,
            })
          )
          return
        }
        console.log("values" , values);
        
        if (isCustom && nodeId) {
          dispatch(
            serviceManagementAction.updateCentroCategoryService({
              data: {
                id: nodeId,
                body: {
                  serviceLevel: 1,
                  nameAr,
                  nameEn,
                  newCategoryInfoId: isCategoryChanged ? selectedCategory?.id : undefined,
                  ...(isCategoryChanged && {b8ShareCategoryInfoId: formValues.categoryInfoId}),
                  ...shared,
                  
                },
              },
              callback: done,
            })
          )
          return
        }

        dispatch(
          serviceManagementAction.saveCentroServiceConfig({
            data: {
              categoryInfoId,
              serviceLevel: 1,
              serviceId: Number(serviceId),
              ...shared,
            },
            callback: done,
          })
        )
      }

      setIsSaving(true)

      if (formValues.icon instanceof File) {
        dispatch(
          serviceManagementAction.uploadCentroCategoryServiceIcon({
            data: buildIconFormData(formValues.icon),
            callback: (uploadedUrl: string | null, uploadError: unknown) => {
              if (uploadError || !uploadedUrl) {
                setIsSaving(false)
                toast.error(
                  getApiErrorMessage(uploadError, t('COMMON.PLEASE_TRY_AGAIN')),
                  ToastDefaultConfig()
                )
                return
              }
              save(stripIconSasToken(uploadedUrl))
            },
          })
        )
        return
      }

      save(stripIconSasToken(typeof formValues.icon === 'string' ? formValues.icon : null))
    },
  })

  const {values, errors, touched, setFieldValue, setFieldTouched, submitForm, resetForm} = formik
  const {
    fileInputRef,
    fileError,
    setFileError,
    iconPreview,
    setIconPreview,
    handleFileChange,
    removeIcon,
    handleSVGClick,
  } = useServiceForm({setFieldValue})

  const [categoryPayment, setCategoryPayment] = useState<CategoryPaymentConfig | null>(null)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentError, setPaymentError] = useState(false)
  useEffect(() => {
    setCategoryPayment(null)
    setPaymentError(false)
    setPaymentLoading(false)
    if (!isShow || !values.categoryInfoId) return
    let active = true
    setPaymentLoading(true)
    dispatch(serviceManagementAction.getCentroCategoryInfoDetail({
      id: values.categoryInfoId,
      callback: (res, err) => {
        if (!active) return
        setPaymentLoading(false)
        setPaymentError(Boolean(err) || !res)
        setCategoryPayment(res?.paymentConfig ?? null)
      },
    }))
    return () => { active = false }
  }, [isShow, values.categoryInfoId, dispatch])

  // Centro Categories for the Category dropdown
  const [categories, setCategories] = useState<CentroCategoryInfoModel[]>([])

  useEffect(() => {
    if (!isShow) return

    dispatch(
      serviceManagementAction.getCategoryOptions({
        data: b8ShareCategoryInfoId ? {} : {sourceType: ''},
        callback: (res) => {
          setCategories((res ?? []) as unknown as CentroCategoryInfoModel[])
        },
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = categories.find(
      (category) => category.b8ShareCategoryInfoId === values.categoryInfoId
    )
    setSelectedCategory(selected)
  }, [isShow])


  const usesTreeLock =
    isEdit || (Boolean(b8ShareCategoryInfoId) && values.categoryInfoId === b8ShareCategoryInfoId)
  const activeLockMessage = usesTreeLock
    ? statusLockMessage
    : selectedCategory && selectedCategory.status === false
    ? t('SERVICE_MANAGEMENT_PAGE.SERVICE_TREE.ACTIVE_LOCKED_BY_PARENT', {
        name: isArabic
          ? selectedCategory.nameAr || selectedCategory.nameEn
          : selectedCategory.nameEn,
      })
    : undefined

  useEffect(() => {
    if (!isEdit && activeLockMessage && values.status) setFieldValue('status', 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLockMessage])

  useEffect(() => {
    if (!isShow) return

    const baseValues = {
      ...initialValues,
      categoryInfoId: b8ShareCategoryInfoId ?? initialValues.categoryInfoId,
      nameEn: initialNameEn ?? initialValues.nameEn,
      nameAr: initialNameAr ?? initialValues.nameAr,
      status: initialStatus === undefined ? initialValues.status : initialStatus ? 1 : 0,
      icon: (initialIcon ?? initialValues.icon) as any,
      visitDuration: initialDuration ?? initialValues.visitDuration,
      numberOfWorkers: initialWorkersNeeded ?? initialValues.numberOfWorkers,
      price: initialPrice ?? initialValues.price,
      priceWithVat:
        initialPrice == null
          ? initialValues.priceWithVat
          : roundMoney(initialPrice * (1 + vatPercent)),
    }

    resetForm({ values: baseValues })
    setFileError('')
    setIconPreview(initialIcon ?? '')

    if (!isEdit || !Number.isFinite(Number(serviceId))) return

    setIsLoadingConfig(true)
    dispatch(
      serviceManagementAction.getCentroServiceConfig({
        
        data: { serviceLevel: 1, serviceId: Number(serviceId), categoryInfoId },
        callback: (res: CentroServiceConfigQry | null, err: unknown) => {
          setIsLoadingConfig(false)

          if (err) {
            toast.error(getApiErrorMessage(err, t('COMMON.PLEASE_TRY_AGAIN')), ToastDefaultConfig())
            return
          }

          if (!res) return

          const isIncl = res.vatTreatment === 1
          const savedPrice = res.price ?? undefined

          const priceExcl =
            savedPrice === undefined
              ? baseValues.price
              : roundMoney(isIncl ? savedPrice / (1 + vatPercent) : savedPrice)
          const priceIncl =
            savedPrice === undefined
              ? baseValues.priceWithVat
              : roundMoney(isIncl ? savedPrice : savedPrice * (1 + vatPercent))

          const isRescheduleIncl = res.rescheduleVatTreatment === 1
          const savedFee = res.rescheduleFeeAmount ?? undefined
          const feeExcl =
            savedFee === undefined
              ? baseValues.rescheduleFeeAmount
              : roundMoney(isRescheduleIncl ? savedFee / (1 + vatPercent) : savedFee)
          const feeIncl =
            savedFee === undefined
              ? baseValues.rescheduleFeeAmountWithVat
              : roundMoney(isRescheduleIncl ? savedFee : savedFee * (1 + vatPercent))

          const keep = <T,>(incoming: T | null | undefined, fallback: T): T =>
            incoming === null || incoming === undefined ? fallback : incoming

          resetForm({
            values: {
              ...baseValues,
              visitDuration: res.duration ?? baseValues.visitDuration,
              numberOfWorkers: res.workersNeeded ?? baseValues.numberOfWorkers,
              price: priceExcl,
              priceWithVat: priceIncl,
              vatTreatment: (isIncl ? 'incl' : 'excl') as VatTreatment,
              status: res.status == null ? baseValues.status : res.status ? 1 : 0,
              subTitleTextEn: res.descriptionEn ?? baseValues.subTitleTextEn,
              subTitleTextAr: res.descriptionAr ?? baseValues.subTitleTextAr,
              icon: res.icon ?? baseValues.icon,
              activeFavoriteWorker: res.activeFavoriteWorker ? 1 : 0,
              receiveB8akOrders: res.receiveB8akOrders ? 1 : 0,
              hasBookingPage: res.hasBookingPage ?? null,
              packageWeeks: keep(res.packageWeeks, baseValues.packageWeeks),
              weeklyVisits: keep(res.weeklyVisits, baseValues.weeklyVisits),
              rescheduleWindowDays: keep(res.rescheduleWindowDays, baseValues.rescheduleWindowDays),
              rescheduleBeforeMinutes: keep(
                res.rescheduleBeforeMinutes,
                baseValues.rescheduleBeforeMinutes
              ),
              rescheduleLimit: keep(res.rescheduleLimit, baseValues.rescheduleLimit),
              oneTimeForAllDays: res.oneTimeForAllDays ? 1 : 0,
              enableRescheduling: res.enableRescheduling ? 1 : 0,
              rescheduleFeeAmount: feeExcl,
              rescheduleFeeAmountWithVat: feeIncl,
              rescheduleVat: (isRescheduleIncl ? 'incl' : 'excl') as VatTreatment,
            },
          })
          setIconPreview(res.icon ?? '')
        },
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, isEdit, serviceId, initialValues])

  const canSave =
    validationSchema.isValidSync(values) && !isLoading && !isSaving && !isLoadingConfig

  const editedName = isArabic ? initialNameAr || initialNameEn : initialNameEn || initialNameAr
  const title = isEdit
    ? `${t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.EDIT_SERVICE_TITLE')}${
        editedName ? ` - ${editedName}` : ''
      }`
    : t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ADD_CUSTOM_SERVICE')

  return (
    <Modal
      show={isShow}
      onHide={onHide}
      backdrop='static'
      keyboard={false}
      centered
      dialogClassName='service-form__dialog'
      contentClassName='service-form__content'
    >
      <div className='service-form'>
        <div className='service-form__header'>
          <h2 className='service-form__title'>{title}</h2>
          <button
            type='button'
            className='service-form__close'
            aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CLOSE')}
            onClick={onHide}
          >
            <KTSVG defaultColor={true} path={FORM_ICONS.close} />
          </button>
        </div>

        <div
          className={`service-form__body${isLoadingConfig ? ' service-form__body--loading' : ''}`}
        >
          {isLoadingConfig && (
            <div className='service-form__loading' role='status' aria-live='polite'>
              <span className='spinner-border' aria-hidden='true' />
              <span className='service-form__loading-text'>{t('COMMON.LOADING')}</span>
            </div>
          )}

          <div className='service-form__banner'>
            <span className='service-form__banner-icon'>
              <KTSVG defaultColor={true} path={FORM_ICONS.info} />
            </span>
            <p className='service-form__banner-text'>
              {t(
                isB8akService
                  ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.B8AK_SERVICE_NOTICE'
                  : sourceType === 'B8AK'
                    ? 'SERVICE_MANAGEMENT_PAGE.CATALOGUE_MODAL.MARKETPLACE_NOTICE'
                    : 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CUSTOM_SERVICE_NOTICE'
              )}
            </p>
          </div>

          <fieldset className='service-form__fieldset'>
          <div className='service-form__grid'>
            <SelectField
              id='major-service-category'
              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CATEGORY')}
              placeholder={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SELECT_CATEGORY')}
              required
              value={values.categoryInfoId}
              options={categories.map((category) => ({
                value: category.b8ShareCategoryInfoId ?? '',
                label: isArabic ? category.nameAr || category.nameEn : category.nameEn,
              }))}
              // A B8ak service always keeps its catalogue Category, even once removed from B8ak.
              disabled={isB8akService}
              error={
                touched.categoryInfoId ? (errors.categoryInfoId as string | undefined) : undefined
              }
              onChange={(value) => {
                setFieldValue('categoryInfoId', value);
                const selected = categories.find(
                  (category) => category.b8ShareCategoryInfoId === value
                )
                setSelectedCategory(selected)
                setIsChangeCategory(true)
                console.log("Category changed");
              }}
            />

            <TextField
              id='major-service-name-en'
              disabled={isB8akService || isRemovedFromB8ak}
              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME')}
              labelHint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ENGLISH')}
              placeholder={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME_EN_PLACEHOLDER')}
              required
              value={values.nameEn}
              error={touched.nameEn ? (errors.nameEn as string | undefined) : undefined}
              onChange={(value) => setFieldValue('nameEn', value)}
              onBlur={() => setFieldTouched('nameEn', true)}
            />
            <TextField
              id='major-service-name-ar'
              disabled={isB8akService || isRemovedFromB8ak}
              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME')}
              labelHint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ARABIC')}
              placeholder={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME_AR_PLACEHOLDER')}
              required
              value={values.nameAr}
              error={touched.nameAr ? (errors.nameAr as string | undefined) : undefined}
              onChange={(value) => setFieldValue('nameAr', value)}
              onBlur={() => setFieldTouched('nameAr', true)}
            />

              <TextAreaField
                id='major-service-desc-en'
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DESCRIPTION')}
                labelHint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ENGLISH')}
                value={values.subTitleTextEn}
                onChange={(value) => setFieldValue('subTitleTextEn', value)}
              />
              <TextAreaField
                id='major-service-desc-ar'
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DESCRIPTION')}
                labelHint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ARABIC')}
                value={values.subTitleTextAr}
                onChange={(value) => setFieldValue('subTitleTextAr', value)}
              />

            <TextField
              id='major-service-duration'
              disabled={isB8akService || isRemovedFromB8ak}
              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DURATION')}
              placeholder={isB8akService ? undefined : '30'}
              suffix={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.MINS')}
              value={values.visitDuration == null ? '' : String(values.visitDuration)}
              error={errors.visitDuration as string | undefined}
              onChange={(value) => setFieldValue('visitDuration', value)}
            />

            <div className='service-form__field service-form__field--full'>
              <FieldLabel label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_ICON')} />
              {/* The remove button is anchored to the frame, so both live in the same box */}
              <div className='service-form__upload-frame'>
                <button
                  type='button'
                  className='service-form__upload'
                  aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.UPLOAD_ICON')}
                  disabled={isRemovedFromB8ak}
                  onClick={handleSVGClick}
                >
                  {iconPreview ? (
                    <img
                      src={iconPreview}
                      alt='Service icon'
                      className='service-form__upload-preview'
                    />
                  ) : (
                    <KTSVG defaultColor={true} path={FORM_ICONS.image} />
                  )}
                </button>
                {iconPreview && !isRemovedFromB8ak && (
                  <button
                    type='button'
                    className='service-form__remove-icon'
                    aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.REMOVE_ICON')}
                    onClick={removeIcon}
                  >
                    ×
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type='file'
                name='icon'
                accept='image/png'
                hidden
                onChange={handleFileChange}
              />
              {fileError && <p className='service-form__error'>{fileError}</p>}
              <p className='service-form__hint'>
                {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_ICON_HINT')}
              </p>
            </div>
          </div>
          </fieldset>

          <div className='service-form__field' style={{ marginTop: '18px' }}>
            <FieldLabel label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VISIBILITY_CHANNELS')} />
            <div className='service-form__visibility'>
              <div className='service-form__visibility-primary'>
                <ToggleRow
                  label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ACTIVE')}
                  checked={Boolean(values.status)}
                  disabled={isRemovedFromB8ak}
                  onChange={() => setFieldValue('status', values.status ? 0 : 1)}
                />
              </div>
              <ToggleRow
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ACTIVE_FAVORITE_WORKER')}
                checked={Boolean(values.activeFavoriteWorker)}
                disabled={isRemovedFromB8ak}
                onChange={() => setFieldValue('activeFavoriteWorker', values.activeFavoriteWorker ? 0 : 1)}
              />
              <ToggleRow
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.BOOKING_PAGE')}
                checked={false}
                disabled
              >
                <span className='service-form__booking-link'>
                  <KTSVG defaultColor={true} path={FORM_ICONS.lock} />
                  {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.BOOKING_PWA_ADD_ON')}
                </span>
              </ToggleRow>
              {/* B8ak Majors only; locked on one no longer in the B8ak catalogue. */}
              {isB8akService && (
                <ToggleRow
                  label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RECEIVE_B8AK_ORDERS')}
                  checked={Boolean(values.receiveB8akOrders)}
                  // A service no longer in the B8ak catalogue keeps its current value
                  disabled={isRemovedFromB8ak}
                  onChange={() =>
                    setFieldValue('receiveB8akOrders', values.receiveB8akOrders ? 0 : 1)
                  }
                >
                  <span className='service-form__info-badge'>
                    <KTSVG defaultColor={true} path={FORM_ICONS.infoBadge} />
                  </span>
                </ToggleRow>
              )}
            </div>
          </div>
          <section style={{marginTop: 24}}>
            <FieldLabel label={t('SERVICE_MANAGEMENT_PAGE.CATEGORY_PAYMENT.TITLE')} />
            {paymentLoading ? <p role='status'>{t('COMMON.LOADING')}</p> : paymentError ? (
              <p role='alert' className='service-form__error'>{t('SERVICE_MANAGEMENT_PAGE.CATEGORY_MODAL.LOAD_FAILED')}</p>
            ) : <CategoryPaymentSettings config={categoryPayment} inherited />}
          </section>
        </div>

        <div className='service-form__footer'>
          <button
            type='button'
            className='service-form__btn service-form__btn--cancel'
            onClick={onHide}
            disabled={isLoading || isSaving}
          >
            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CANCEL')}
          </button>
          <button
            type='button'
            className='service-form__btn service-form__btn--secondary'
            onClick={onOpenAdvancedPricing}
            disabled={isLoading || isSaving}
          >
            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ADVANCED_PRICING')}
          </button>
          <button
            type='button'
            className='service-form__btn service-form__btn--save'
            disabled={!canSave}
            onClick={submitForm}
          >
            {isSaving ? t('COMMON.SAVING') : t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAVE')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default AddMajorServiceModal
